import { type ErrorType, apiRequest } from "../api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type CallStatus = "ringing" | "active" | "ended" | "missed" | "declined";
export type CallRecordingStatus =
  | "none"
  | "recording"
  | "processing"
  | "ready"
  | "failed";

export interface CallRecordingAttachment {
  url: string;
  name: string;
  mimeType: string;
  size?: number;
  transcript?: string;
}

export interface Call {
  _id: string;
  organizationId: string;
  ticketId?: string;
  conversationId?: string;
  agentId: string;
  contactId: string;
  status: CallStatus;
  answeredAt?: string;
  endedAt?: string;
  endedReason?: string;
  recordingStatus: CallRecordingStatus;
  recordingAttachment?: CallRecordingAttachment;
  createdAt: string;
  updatedAt: string;
}

export interface LiveKitTokenResponse {
  url: string;
  token: string;
}

export interface CreateCallPayload {
  /** Opcional — quando ausente, o backend resolve o contato pelo ticket/conversa de origem. */
  contactId?: string;
  ticketId?: string;
  conversationId?: string;
}

// ─── Acesso remoto (sempre dentro de uma chamada ativa) ───────────────────────

export type RemoteSessionStatus =
  | "pending_install"
  | "waiting_device"
  | "connected"
  | "in_progress"
  | "ended"
  | "expired"
  | "error";

export interface RemoteSession {
  _id: string;
  organizationId: string;
  callId: string;
  ticketId?: string;
  agentId: string;
  createdBy: string;
  status: RemoteSessionStatus;
  meshNodeId?: string;
  installInviteUrl?: string;
  platform?: "windows" | "mac" | "linux" | "temp";
  connectedAt?: string;
  endedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StartRemoteSessionResponse {
  success: boolean;
  result: RemoteSession;
}

export interface ViewerTokenResponse {
  success: boolean;
  result: { viewerUrl?: string; expiresAt: string };
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const callService = {
  /** Inicia uma videochamada (status inicial `ringing`) com o contato do ticket/conversa. */
  async create(
    payload: CreateCallPayload
  ): Promise<[Call | null, ErrorType | null]> {
    return apiRequest<Call>({
      loaderKey: "createCall",
      method: "post",
      url: "/app/calls",
      data: payload,
    });
  },

  /** Busca a chamada por id — usado pra abrir a aba de atendimento (Meet-style) direto pelo callId. */
  async getById(id: string): Promise<[Call | null, ErrorType | null]> {
    return apiRequest<Call>({
      loaderKey: "getCallById",
      method: "get",
      url: `/app/calls/${id}`,
    });
  },

  async hangup(
    id: string,
    reason?: string
  ): Promise<[Call | null, ErrorType | null]> {
    return apiRequest<Call>({
      loaderKey: "hangupCall",
      method: "post",
      url: `/app/calls/${id}/hangup`,
      data: { reason },
    });
  },

  /** Token de acesso LiveKit (URL do servidor + JWT) pra entrar na sala da chamada. */
  async getLiveKitToken(
    id: string
  ): Promise<[LiveKitTokenResponse | null, ErrorType | null]> {
    return apiRequest<LiveKitTokenResponse>({
      loaderKey: "getCallLiveKitToken",
      method: "get",
      url: `/app/calls/${id}/livekit-token`,
    });
  },

  /** Gera um link de convite (sem login) pra mais alguém entrar na mesma sala — só com a call `active`. */
  async createInvite(id: string): Promise<[{ url: string } | null, ErrorType | null]> {
    return apiRequest<{ url: string }>({
      loaderKey: "createCallInvite",
      method: "post",
      url: `/app/calls/${id}/invite`,
    });
  },

  /** Inicia uma sessão de acesso remoto durante a chamada — o link de instalação chega pelo chat da própria call. */
  async startRemoteAccess(
    callId: string
  ): Promise<[StartRemoteSessionResponse | null, ErrorType | null]> {
    return apiRequest<StartRemoteSessionResponse>({
      loaderKey: "startCallRemoteAccess",
      method: "post",
      url: `/app/remote-access/calls/${callId}/start`,
    });
  },

  /** Minta o token de embed do viewer do MeshCentral — só funciona depois do device conectar. */
  async getRemoteAccessViewerToken(
    callId: string,
    sessionId: string
  ): Promise<[ViewerTokenResponse | null, ErrorType | null]> {
    return apiRequest<ViewerTokenResponse>({
      loaderKey: "getCallRemoteAccessViewerToken",
      method: "post",
      url: `/app/remote-access/calls/${callId}/sessions/${sessionId}/viewer-token`,
    });
  },

  async endRemoteAccess(
    callId: string,
    sessionId: string,
    reason?: string
  ): Promise<[{ success: boolean; result: RemoteSession } | null, ErrorType | null]> {
    return apiRequest<{ success: boolean; result: RemoteSession }>({
      loaderKey: "endCallRemoteAccess",
      method: "post",
      url: `/app/remote-access/calls/${callId}/sessions/${sessionId}/end`,
      data: { reason },
    });
  },
};
